import React, { ReactElement, forwardRef, useEffect, useRef } from "react";
import Button from "../button";
import { ButtonSize, MessageStatus } from "../types";
import { ChangeMethodType } from "./tag-input.types";
import { useCombinedRefs } from "../utils/useCombinedRefs";

interface TagListType {
  index: number;
  statusValue?: string[];
  tag: string;
  status?: MessageStatus | "success" | "warning" | "error" | "info";
  ariaLabel: string;
  removeTag: (e: ChangeMethodType, removeTag: number) => void;
  isFocus?: boolean;
}

export const ListItem = forwardRef<HTMLLIElement, TagListType>(
  (
    { index, statusValue, tag, status, ariaLabel, removeTag, isFocus, ...rest },
    containerExternalRef
  ): ReactElement<HTMLLIElement> => {
    const tagRef = useRef<HTMLLIElement>(null);

    useCombinedRefs(tagRef, containerExternalRef);

    useEffect(() => {
      if (isFocus) {
        tagRef?.current?.focus();
      }
    }, [index, isFocus]);

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLLIElement> | React.KeyboardEvent
    ): void => {
      switch (e.key) {
        case "Backspace":
          removeTag(e as ChangeMethodType, index);
          break;
      }
    };

    return (
      <li
        ref={tagRef}
        key={`spark-tag-${index + 1}`}
        className={`spark-tag-input__tag ${isFocus ? "focus" : ""}`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        // display status styling to matching tags
        // important to reset to null when status should be off
        {...(statusValue?.includes(tag) ? { [`data-${status}`]: true } : null)}
        {...rest}
      >
        <span className="spark-tag-input__tag__label">{tag}</span>
        <Button
          ariaLabel={ariaLabel}
          icon="spark-icon-close"
          onClick={(e): void => removeTag(e, index)}
          size={ButtonSize.EXTRA_SMALL}
          tabIndex={-1}
        />
      </li>
    );
  }
);

ListItem.displayName = "ListItem";
